import { Capacitor } from '@capacitor/core';
import { App } from '@capacitor/app';
import { isBundledMobileApp } from '@/mobile/native/apiBase';
import { getLastPullAt } from '@/mobile/sync/fieldSyncCache';
import { runFieldSync } from '@/mobile/sync/useFieldSync';

export const FIELD_CACHE_STALE_MS = 24 * 60 * 60 * 1000;
export const FIELD_SOFT_PULL_MIN_AGE_MS = 5 * 60 * 1000;

let lifecycleStarted = false;
let softPullTimer: ReturnType<typeof setTimeout> | null = null;

export async function readFieldCacheAgeMs(): Promise<number | null> {
  const lastPullAt = await getLastPullAt();
  if (!lastPullAt) {
    return null;
  }

  return Date.now() - new Date(lastPullAt).getTime();
}

export async function isFieldCacheStale(
  maxAgeMs = FIELD_CACHE_STALE_MS,
): Promise<boolean> {
  const age = await readFieldCacheAgeMs();
  if (age === null) {
    return true;
  }

  return age > maxAgeMs;
}

/**
 * Pull incremental discreto (ex.: ao abrir a lista) se o último pull foi há mais de X.
 */
export function scheduleSoftFieldPull(delayMs = 2_000, minAgeMs = FIELD_SOFT_PULL_MIN_AGE_MS): void {
  if (!isBundledMobileApp() || !Capacitor.isNativePlatform()) {
    return;
  }

  if (softPullTimer) {
    clearTimeout(softPullTimer);
  }

  softPullTimer = setTimeout(() => {
    softPullTimer = null;
    void runSoftFieldPullIfStale(minAgeMs);
  }, delayMs);
}

export async function runSoftFieldPullIfStale(
  minAgeMs = FIELD_SOFT_PULL_MIN_AGE_MS,
): Promise<void> {
  if (!isBundledMobileApp() || !Capacitor.isNativePlatform()) {
    return;
  }

  const age = await readFieldCacheAgeMs();
  if (age !== null && age < minAgeMs) {
    return;
  }

  await runFieldSync({ pull: true });
}

export function startBundledFieldSyncLifecycle(): void {
  if (!isBundledMobileApp() || !Capacitor.isNativePlatform() || lifecycleStarted) {
    return;
  }

  lifecycleStarted = true;

  const onHidden = () => {
    void runSoftFieldPullIfStale(FIELD_SOFT_PULL_MIN_AGE_MS);
  };

  if (typeof document !== 'undefined') {
    document.addEventListener('visibilitychange', () => {
      if (document.visibilityState === 'hidden') {
        onHidden();
      }
    });
  }

  void App.addListener('pause', () => {
    onHidden();
  });
}
